'use client';

import { Suspense, useMemo } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import { withDyeQuery } from '@/utils/router/with-dye-query';

import type { ComponentProps, ReactNode } from 'react';

type TNextLinkProps = ComponentProps<typeof Link>;

export interface IAppLinkProps extends Omit<TNextLinkProps, 'href'> {
    /** 站内路径（字符串）；会自动合并当前页落地相关五参 */
    href: string;
    /** 是否透传落地相关五参，默认 true */
    preserveDye?: boolean;
    children?: ReactNode;
}

function AppLinkInner(props: IAppLinkProps) {
    const { href, preserveDye = true, children, ...rest } = props;
    const searchParams = useSearchParams();

    const mergedHref = useMemo(() => {
        if (!preserveDye) {
            return href;
        }
        return withDyeQuery(href, searchParams);
    }, [href, preserveDye, searchParams]);

    return (
        <Link href={mergedHref} {...rest}>
            {children}
        </Link>
    );
}

/**
 * 声明式导航：在 next/link 基础上默认透传落地相关五参（染色四参 + landing_timestamp）。
 * 内部 Suspense + useSearchParams，避免静态导出 / SSR 与客户端 href 不一致。
 */
export default function AppLink(props: IAppLinkProps) {
    const { href, preserveDye = true, children, ...rest } = props;

    return (
        <Suspense
            fallback={
                <Link href={href} {...rest}>
                    {children}
                </Link>
            }
        >
            <AppLinkInner href={href} preserveDye={preserveDye} {...rest}>
                {children}
            </AppLinkInner>
        </Suspense>
    );
}
